import {
  adjacent,
  countWords,
  headingFlags,
  isCapitalizedMidSentence,
  isHyphenated,
  ratePer,
  type Sentence,
  sentenceIndexAt,
  splitSentences,
  stripPossessive,
  type Token,
  wordTokens,
} from "@/lib/tool-kit/writing-analysis/text";

/**
 * Nominalizations — the nouns Helen Sword calls "zombie nouns" — built from a verb or an
 * adjective: decide → decision, important → importance. A sentence that runs on them
 * ("the implementation of the evaluation") hides who did what in abstract nouns and
 * props the action up on a weak verb ("make a decision"). Plenty of nominalizations are
 * exactly the right word, so this module only ever offers a prompt.
 *
 * Precision over recall, in three tiers:
 *
 * 1. **Phrases** — a light verb carrying a nominalization ("conduct an investigation
 *    of", "is in agreement with", "a decision was made"). The strongest signal.
 * 2. **Known nouns** — a curated list of common academic nominalizations with the verb
 *    or adjective inside each. Field terms are exempted by the word before or after
 *    them ("thematic analysis", "academic achievement", "exclusion criteria").
 * 3. **Possible** — any other -tion, -sion, -ment, -ance, -ence, -ity or -ness noun of
 *    eight letters or more, but only in the zombie frame "the ___ of", and never one of
 *    the everyday nouns in `EVERYDAY_NOUNS`.
 *
 * A capitalized word mid-sentence is taken to be part of a name ("the Food and Drug
 * Administration") and a hyphenated compound ("decision-making") is left alone.
 */

export type NominalizationKind = "phrase" | "noun" | "possible";

export interface NominalizationMatch {
  start: number;
  end: number;
  text: string;
  kind: NominalizationKind;
  /** The noun at the heart of the match, singular and lowercase. */
  noun: string;
  /** The verb or adjective to try instead ("investigate", "be important"); "" if unknown. */
  suggestion: string;
}

export interface DenseSentence {
  start: number;
  end: number;
  text: string;
  count: number;
}

export interface NounTally {
  noun: string;
  count: number;
  suggestion: string;
  kind: NominalizationKind;
}

export interface NominalizationReport {
  words: number;
  matches: NominalizationMatch[];
  phrases: number;
  nouns: number;
  possible: number;
  /** Nominalizations per 100 words; a phrase counts once. */
  density: number;
  /** Sentences carrying three or more, densest first. */
  denseSentences: DenseSentence[];
  /** One row per noun, most frequent first. */
  byNoun: NounTally[];
}

/* ------------------------------------------------------------------ data */

/** Noun → the verb inside it, with the preposition the verb takes, if any. */
const VERB_NOUNS: Readonly<Record<string, string>> = {
  acceleration: "accelerate",
  acceptance: "accept",
  accumulation: "accumulate",
  achievement: "achieve",
  acknowledgment: "acknowledge",
  acquisition: "acquire",
  adjustment: "adjust",
  administration: "administer",
  adoption: "adopt",
  advancement: "advance",
  agreement: "agree with",
  alignment: "align with",
  allocation: "allocate",
  alteration: "alter",
  analysis: "analyze",
  announcement: "announce",
  anticipation: "anticipate",
  appearance: "appear",
  application: "apply",
  appreciation: "appreciate",
  approval: "approve",
  arrangement: "arrange",
  arrival: "arrive",
  articulation: "articulate",
  assertion: "assert",
  assessment: "assess",
  assistance: "assist",
  association: "associate with",
  assumption: "assume",
  attainment: "attain",
  avoidance: "avoid",
  calculation: "calculate",
  categorization: "categorize",
  characterization: "characterize",
  clarification: "clarify",
  classification: "classify",
  collaboration: "collaborate with",
  combination: "combine",
  commitment: "commit to",
  communication: "communicate",
  comparison: "compare",
  compilation: "compile",
  completion: "complete",
  compliance: "comply with",
  computation: "compute",
  confirmation: "confirm",
  connection: "connect",
  consideration: "consider",
  consolidation: "consolidate",
  consultation: "consult",
  continuation: "continue",
  contribution: "contribute to",
  conversion: "convert",
  cooperation: "cooperate with",
  coordination: "coordinate",
  correction: "correct",
  creation: "create",
  decision: "decide",
  declaration: "declare",
  deduction: "deduce",
  definition: "define",
  deletion: "delete",
  deliberation: "deliberate",
  delivery: "deliver",
  demonstration: "demonstrate",
  denial: "deny",
  dependence: "depend on",
  depiction: "depict",
  description: "describe",
  destruction: "destroy",
  detection: "detect",
  determination: "determine",
  development: "develop",
  differentiation: "differentiate",
  disappearance: "disappear",
  discovery: "discover",
  discussion: "discuss",
  dismissal: "dismiss",
  distinction: "distinguish",
  documentation: "document",
  domination: "dominate",
  elaboration: "elaborate on",
  elimination: "eliminate",
  emergence: "emerge",
  emphasis: "emphasize",
  encouragement: "encourage",
  endorsement: "endorse",
  enhancement: "enhance",
  establishment: "establish",
  estimation: "estimate",
  evaluation: "evaluate",
  examination: "examine",
  exclusion: "exclude",
  execution: "execute",
  existence: "exist",
  expansion: "expand",
  expectation: "expect",
  experimentation: "experiment",
  explanation: "explain",
  exploitation: "exploit",
  exploration: "explore",
  extension: "extend",
  facilitation: "facilitate",
  failure: "fail",
  formation: "form",
  formulation: "formulate",
  fulfillment: "fulfill",
  generalization: "generalize",
  identification: "identify",
  illustration: "illustrate",
  implementation: "implement",
  improvement: "improve",
  inclusion: "include",
  indication: "indicate",
  inference: "infer",
  inhibition: "inhibit",
  initiation: "initiate",
  insistence: "insist on",
  inspection: "inspect",
  integration: "integrate",
  intention: "intend",
  interference: "interfere with",
  interpretation: "interpret",
  introduction: "introduce",
  investigation: "investigate",
  involvement: "involve",
  judgment: "judge",
  justification: "justify",
  maintenance: "maintain",
  management: "manage",
  measurement: "measure",
  minimization: "minimize",
  modification: "modify",
  negotiation: "negotiate",
  notification: "notify",
  occurrence: "occur",
  omission: "omit",
  opposition: "oppose",
  optimization: "optimize",
  participation: "participate in",
  perception: "perceive",
  persistence: "persist",
  persuasion: "persuade",
  postponement: "postpone",
  preference: "prefer",
  preparation: "prepare",
  presentation: "present",
  preservation: "preserve",
  prevention: "prevent",
  prohibition: "prohibit",
  promotion: "promote",
  proposal: "propose",
  protection: "protect",
  provision: "provide",
  punishment: "punish",
  quantification: "quantify",
  realization: "realize",
  recognition: "recognize",
  recommendation: "recommend",
  reconstruction: "reconstruct",
  recovery: "recover",
  reduction: "reduce",
  referral: "refer",
  reflection: "reflect on",
  refusal: "refuse",
  rejection: "reject",
  reliance: "rely on",
  removal: "remove",
  replacement: "replace",
  representation: "represent",
  reproduction: "reproduce",
  requirement: "require",
  resolution: "resolve",
  restoration: "restore",
  restriction: "restrict",
  retention: "retain",
  retrieval: "retrieve",
  revelation: "reveal",
  revision: "revise",
  selection: "select",
  separation: "separate",
  simplification: "simplify",
  specification: "specify",
  speculation: "speculate about",
  stabilization: "stabilize",
  standardization: "standardize",
  stimulation: "stimulate",
  submission: "submit",
  substitution: "substitute",
  suggestion: "suggest",
  summarization: "summarize",
  supervision: "supervise",
  termination: "terminate",
  transformation: "transform",
  transmission: "transmit",
  usage: "use",
  utilization: "use",
  validation: "validate",
  verification: "verify",
  violation: "violate",
};

/** Noun → the adjective inside it: "the importance of" → "is important". */
const ADJECTIVE_NOUNS: Readonly<Record<string, string>> = {
  acceptability: "acceptable",
  adequacy: "adequate",
  affordability: "affordable",
  ambiguity: "ambiguous",
  applicability: "applicable",
  appropriateness: "appropriate",
  availability: "available",
  awareness: "aware",
  capability: "capable",
  clarity: "clear",
  comparability: "comparable",
  compatibility: "compatible",
  completeness: "complete",
  complexity: "complex",
  consistency: "consistent",
  correctness: "correct",
  desirability: "desirable",
  difficulty: "difficult",
  effectiveness: "effective",
  efficiency: "efficient",
  eligibility: "eligible",
  fairness: "fair",
  feasibility: "feasible",
  flexibility: "flexible",
  importance: "important",
  impossibility: "impossible",
  inability: "unable",
  inadequacy: "inadequate",
  inconsistency: "inconsistent",
  ineffectiveness: "ineffective",
  inefficiency: "inefficient",
  inevitability: "inevitable",
  insufficiency: "insufficient",
  necessity: "necessary",
  permanence: "permanent",
  popularity: "popular",
  possibility: "possible",
  predictability: "predictable",
  presence: "present",
  profitability: "profitable",
  prominence: "prominent",
  readiness: "ready",
  relevance: "relevant",
  reluctance: "reluctant",
  responsibility: "responsible",
  robustness: "robust",
  scarcity: "scarce",
  seriousness: "serious",
  severity: "severe",
  significance: "significant",
  similarity: "similar",
  simplicity: "simple",
  sufficiency: "sufficient",
  suitability: "suitable",
  susceptibility: "susceptible",
  unavailability: "unavailable",
  uncertainty: "uncertain",
  unwillingness: "unwilling",
  urgency: "urgent",
  usefulness: "useful",
  vulnerability: "vulnerable",
  willingness: "willing",
};

/** Light verbs, by lemma, that turn an ordinary noun into a zombie phrase. */
const MAKE = ["make"];
const RESEARCH = ["carry out", "conduct", "perform", "undertake"];
const SHIFT = [
  "achieve",
  "bring about",
  "cause",
  "exhibit",
  "lead to",
  "result in",
  "show",
  "there be",
];
const SUPPLY = ["give", "offer", "provide"];

/**
 * Nouns that are fine on their own ("the study", "an effect", "the conclusion") and
 * zombies only behind particular light verbs: "conduct a study of" → "study", "have an
 * effect on" → "affect", "reach a conclusion" → "conclude" — but not "had a clear
 * conclusion". Each noun lists the suggestion and the light verbs (by lemma) it pairs with.
 */
const PHRASE_ONLY_NOUNS: Readonly<
  Record<string, readonly [string, readonly string[]]>
> = {
  action: ["act", ["take"]],
  answer: ["answer", SUPPLY],
  argument: ["argue", MAKE],
  attempt: ["try", MAKE],
  belief: ["believe", ["express", "have", "hold"]],
  change: ["change", [...MAKE, ...SHIFT]],
  check: ["check", [...MAKE, ...RESEARCH]],
  choice: ["choose", MAKE],
  claim: ["claim", MAKE],
  comment: ["comment on", MAKE],
  complaint: ["complain", MAKE],
  conclusion: ["conclude", ["arrive at", "come to", "draw", "reach"]],
  contact: ["contact", MAKE],
  decline: ["decline", SHIFT],
  decrease: ["decrease", SHIFT],
  effect: ["affect", ["have"]],
  effort: ["try", MAKE],
  estimate: ["estimate", [...MAKE, ...SUPPLY]],
  guess: ["guess", MAKE],
  impact: ["affect", ["have"]],
  increase: ["increase", SHIFT],
  influence: ["influence", ["have"]],
  information: ["inform", SUPPLY],
  inquiry: ["inquire into", [...MAKE, ...RESEARCH]],
  knowledge: ["know", ["gain", "have"]],
  look: ["look at", ["have", "take"]],
  mention: ["mention", MAKE],
  need: ["need", ["express", "have", "there be"]],
  objection: ["object to", ["express", "make", "raise"]],
  observation: ["observe", MAKE],
  offer: ["offer", MAKE],
  plan: ["plan", MAKE],
  prediction: ["predict", MAKE],
  priority: ["prioritize", ["give"]],
  promise: ["promise", MAKE],
  reference: ["refer to", MAKE],
  request: ["request", MAKE],
  response: ["respond to", [...MAKE, ...SUPPLY]],
  review: ["review", RESEARCH],
  search: ["search", RESEARCH],
  statement: ["state", MAKE],
  study: ["study", RESEARCH],
  summary: ["summarize", SUPPLY],
  support: ["support", SUPPLY],
  survey: ["survey", RESEARCH],
  tendency: ["tend to", ["have", "show"]],
  test: ["test", RESEARCH],
  understanding: ["understand", ["gain", "have"]],
  use: ["use", MAKE],
};

/** "is of importance" → "is important". Only after a form of "be" + "of". */
const BE_OF_NOUNS: Readonly<Record<string, string>> = {
  benefit: "beneficial",
  help: "helpful",
  importance: "important",
  interest: "interesting",
  necessity: "necessary",
  note: "notable",
  relevance: "relevant",
  significance: "significant",
  use: "useful",
  value: "valuable",
};

/** "is in agreement with" → "agrees with". Only after a form of "be" + "in". */
const BE_IN_NOUNS: Readonly<Record<string, string>> = {
  accordance: "follow",
  agreement: "agree with",
  alignment: "align with",
  compliance: "comply with",
  conflict: "conflict with",
  contradiction: "contradict",
  favor: "favor",
  favour: "favour",
  need: "need",
  opposition: "oppose",
  possession: "have",
  support: "support",
  violation: "violate",
};

/**
 * Field terms: a curated noun is not flagged after these words. "Thematic analysis" and
 * "academic achievement" name a method and a construct, not a hidden action.
 */
const EXEMPT_AFTER: Readonly<Record<string, readonly string[]>> = {
  achievement: [
    "academic",
    "educational",
    "math",
    "reading",
    "school",
    "student",
  ],
  acquisition: ["data", "language", "skill", "vocabulary"],
  adjustment: ["emotional", "marital", "psychological", "social"],
  administration: [
    "business",
    "health",
    "hospital",
    "public",
    "school",
    "university",
  ],
  adoption: ["child", "international"],
  agreement: [
    "collective",
    "interrater",
    "inter-rater",
    "license",
    "licence",
    "peace",
    "rater",
    "service",
    "subject-verb",
    "trade",
    "user",
  ],
  analysis: [
    "bivariate",
    "cluster",
    "comparative",
    "content",
    "cost",
    "data",
    "discourse",
    "factor",
    "mediation",
    "meta",
    "moderation",
    "multivariate",
    "narrative",
    "network",
    "path",
    "policy",
    "power",
    "principal",
    "qualitative",
    "quantitative",
    "regression",
    "risk",
    "secondary",
    "sensitivity",
    "spectral",
    "statistical",
    "subgroup",
    "survival",
    "systems",
    "textual",
    "thematic",
    "univariate",
    "variance",
  ],
  appearance: ["facial", "personal", "physical"],
  application: [
    "college",
    "computer",
    "grant",
    "job",
    "loan",
    "mobile",
    "patent",
    "smartphone",
    "software",
    "university",
    "visa",
    "web",
  ],
  approval: [
    "board",
    "ethical",
    "ethics",
    "fda",
    "institutional",
    "irb",
    "regulatory",
  ],
  arrangement: [
    "care",
    "childcare",
    "custody",
    "financial",
    "housing",
    "living",
  ],
  assessment: [
    "clinical",
    "environmental",
    "formative",
    "health",
    "impact",
    "needs",
    "psychological",
    "risk",
    "summative",
  ],
  association: ["free"],
  assumption: [
    "homogeneity",
    "independence",
    "linearity",
    "normality",
    "sphericity",
  ],
  attainment: ["academic", "educational", "goal"],
  availability: ["code", "data"],
  avoidance: ["experiential", "tax"],
  awareness: ["metacognitive", "phonological", "public", "situational"],
  capability: ["dynamic"],
  combination: ["in"],
  commitment: ["affective", "continuance", "normative", "organizational"],
  communication: [
    "business",
    "crisis",
    "digital",
    "health",
    "interpersonal",
    "mass",
    "nonverbal",
    "oral",
    "organizational",
    "personal",
    "political",
    "risk",
    "science",
    "scholarly",
    "strategic",
    "verbal",
    "written",
  ],
  comparison: ["by", "in", "multiple", "pairwise", "planned"],
  completion: ["course", "degree", "program", "school", "task"],
  complexity: ["algorithmic", "computational", "sample", "space", "time"],
  connection: ["broadband", "internet", "network", "social", "wireless"],
  consistency: ["internal"],
  consolidation: ["memory", "sleep"],
  contribution: ["campaign", "charitable", "financial", "pension", "political"],
  coordination: ["eye-hand", "hand-eye", "motor"],
  correction: ["bonferroni", "continuity", "error", "holm", "yates"],
  deduction: ["tax"],
  definition: ["by", "conceptual", "operational", "working"],
  deletion: ["chromosomal", "gene", "genetic", "listwise", "pairwise"],
  delivery: ["care", "curriculum", "drug", "health", "home", "service"],
  dependence: [
    "alcohol",
    "drug",
    "field",
    "nicotine",
    "opioid",
    "path",
    "substance",
  ],
  description: ["job"],
  desirability: ["social"],
  detection: ["anomaly", "early", "fraud", "outlier", "signal"],
  development: [
    "adolescent",
    "and",
    "career",
    "child",
    "cognitive",
    "community",
    "early",
    "economic",
    "emotional",
    "human",
    "international",
    "language",
    "motor",
    "personal",
    "professional",
    "rural",
    "social",
    "software",
    "sustainable",
    "urban",
  ],
  difficulty: ["item", "task"],
  differentiation: ["cell", "cellular", "product"],
  discovery: ["drug", "scientific"],
  discussion: ["class", "group", "panel"],
  distinction: ["with"],
  efficiency: [
    "allocative",
    "computational",
    "economic",
    "energy",
    "fuel",
    "statistical",
    "technical",
  ],
  estimation: [
    "bayesian",
    "density",
    "interval",
    "kernel",
    "likelihood",
    "parameter",
    "point",
  ],
  evaluation: ["course", "program", "programme", "teacher"],
  examination: [
    "clinical",
    "entrance",
    "eye",
    "final",
    "medical",
    "neurological",
    "oral",
    "pelvic",
    "physical",
    "postmortem",
    "written",
  ],
  exclusion: ["social"],
  facilitation: ["social"],
  failure: [
    "cardiac",
    "heart",
    "kidney",
    "liver",
    "market",
    "organ",
    "renal",
    "respiratory",
  ],
  flexibility: ["cognitive", "mental", "psychological"],
  formation: [
    "attitude",
    "coalition",
    "concept",
    "habit",
    "identity",
    "in",
    "rock",
    "star",
  ],
  generalization: ["stimulus"],
  identification: ["photo", "social"],
  improvement: ["quality"],
  inclusion: ["financial", "social"],
  inference: ["bayesian", "causal", "deductive", "inductive", "statistical"],
  inhibition: [
    "behavioral",
    "behavioural",
    "competitive",
    "enzyme",
    "latent",
    "lateral",
    "response",
  ],
  insufficiency: ["adrenal", "cardiac", "renal", "venous"],
  integration: [
    "economic",
    "horizontal",
    "numerical",
    "regional",
    "sensory",
    "social",
    "vertical",
  ],
  intention: [
    "behavioral",
    "behavioural",
    "implementation",
    "purchase",
    "turnover",
  ],
  interference: ["proactive", "retroactive", "signal", "wave"],
  involvement: [
    "civic",
    "community",
    "family",
    "parent",
    "parental",
    "political",
    "student",
  ],
  judgment: ["clinical", "moral", "professional", "value"],
  maintenance: ["home", "road", "weight"],
  management: [
    "anger",
    "business",
    "change",
    "classroom",
    "data",
    "disease",
    "financial",
    "knowledge",
    "middle",
    "pain",
    "project",
    "quality",
    "resource",
    "risk",
    "senior",
    "strategic",
    "stress",
    "supply",
    "time",
    "top",
    "upper",
    "waste",
    "weight",
  ],
  modification: ["behavior", "behaviour", "genetic"],
  optimization: ["convex", "numerical", "search"],
  participation: [
    "civic",
    "community",
    "force",
    "labor",
    "labour",
    "political",
    "social",
  ],
  perception: [
    "auditory",
    "color",
    "colour",
    "depth",
    "risk",
    "speech",
    "visual",
  ],
  permanence: ["object"],
  persistence: ["academic", "student", "task"],
  preparation: ["sample", "surface", "teacher", "test"],
  presentation: ["clinical", "conference", "oral", "poster"],
  prevention: [
    "crime",
    "disease",
    "fall",
    "falls",
    "injury",
    "primary",
    "relapse",
    "secondary",
    "suicide",
    "tertiary",
    "violence",
  ],
  promotion: ["career", "health", "job"],
  proposal: [
    "budget",
    "dissertation",
    "grant",
    "project",
    "research",
    "thesis",
  ],
  protection: ["child", "consumer", "data", "environmental", "privacy"],
  punishment: ["capital", "corporal"],
  readiness: ["kindergarten", "school"],
  recognition: [
    "emotion",
    "face",
    "facial",
    "image",
    "object",
    "pattern",
    "speech",
    "voice",
    "word",
  ],
  recommendation: ["of"],
  recovery: ["data", "disaster", "economic"],
  reduction: ["data", "dimension", "dimensionality", "harm", "noise", "risk"],
  reflection: ["light", "total"],
  rejection: ["peer", "social", "transplant"],
  replacement: ["hip", "hormone", "joint", "knee", "valve"],
  representation: ["cognitive", "internal", "mental"],
  reproduction: ["asexual", "cultural", "sexual", "social"],
  resolution: [
    "conflict",
    "dispute",
    "high",
    "image",
    "low",
    "screen",
    "spatial",
    "temporal",
  ],
  responsibility: ["corporate", "moral", "social"],
  restoration: ["ecological", "habitat"],
  restriction: ["caloric", "calorie", "dietary", "range", "sleep"],
  retention: ["customer", "employee", "memory", "staff", "student", "teacher"],
  retrieval: ["information", "memory"],
  selection: [
    "adverse",
    "feature",
    "group",
    "kin",
    "model",
    "natural",
    "sexual",
    "variable",
  ],
  separation: ["marital"],
  severity: ["disease", "illness", "symptom"],
  significance: ["clinical", "practical", "statistical"],
  similarity: ["cosine", "semantic"],
  specification: ["model"],
  stimulation: [
    "brain",
    "cognitive",
    "electrical",
    "magnetic",
    "nerve",
    "sensory",
  ],
  supervision: ["clinical"],
  susceptibility: ["genetic", "magnetic"],
  transformation: ["data", "digital", "log", "logarithmic"],
  transmission: [
    "community",
    "cultural",
    "disease",
    "hiv",
    "intergenerational",
    "sexual",
    "vertical",
    "viral",
    "virus",
  ],
  uncertainty: ["measurement", "standard"],
  usefulness: ["perceived"],
  utilization: ["care", "health", "healthcare", "service", "services"],
};

/** Field terms by the word after: "exclusion criteria", "feasibility study". */
const EXEMPT_BEFORE: Readonly<Record<string, readonly string[]>> = {
  decision: [
    "maker",
    "makers",
    "rule",
    "rules",
    "support",
    "theory",
    "tree",
    "trees",
  ],
  eligibility: ["criteria", "criterion", "requirements"],
  exclusion: ["criteria", "criterion"],
  feasibility: ["studies", "study", "trial", "trials"],
  implementation: ["fidelity", "outcomes", "research", "science"],
  inclusion: ["criteria", "criterion"],
  measurement: [
    "error",
    "errors",
    "instrument",
    "instruments",
    "invariance",
    "level",
    "levels",
    "model",
    "models",
    "properties",
    "scale",
    "scales",
    "tool",
    "tools",
  ],
  robustness: ["analyses", "analysis", "check", "checks", "test", "tests"],
  separation: ["anxiety"],
  significance: ["level", "levels", "test", "tests", "threshold"],
};

/**
 * Nouns with a nominalizing suffix that are everyday or technical vocabulary rather
 * than hidden actions. Only consulted for tier-3 "possible" matches.
 */
const EVERYDAY_NOUNS = new Set([
  "abundance",
  "accommodation",
  "activity",
  "adherence",
  "adolescence",
  "affiliation",
  "allegation",
  "alliance",
  "allowance",
  "ambulance",
  "amendment",
  "ammunition",
  "annotation",
  "apartment",
  "argument",
  "assignment",
  "attendance",
  "attention",
  "audience",
  "authority",
  "balance",
  "basement",
  "brightness",
  "business",
  "capacity",
  "celebrity",
  "charity",
  "circulation",
  "citation",
  "citizenship",
  "civilization",
  "coalition",
  "coherence",
  "collection",
  "commission",
  "commodity",
  "community",
  "comparison",
  "compartment",
  "competence",
  "competition",
  "complement",
  "composition",
  "comprehension",
  "concentration",
  "condition",
  "conference",
  "confidence",
  "configuration",
  "conjunction",
  "consciousness",
  "consequence",
  "constitution",
  "construction",
  "consumption",
  "convention",
  "convergence",
  "conversation",
  "corporation",
  "correlation",
  "correspondence",
  "creativity",
  "credibility",
  "curiosity",
  "darkness",
  "deficiency",
  "democracy",
  "density",
  "department",
  "destination",
  "deviation",
  "difference",
  "dimension",
  "disability",
  "distance",
  "distribution",
  "divergence",
  "diversity",
  "division",
  "document",
  "duration",
  "education",
  "election",
  "electricity",
  "element",
  "emission",
  "emotion",
  "employment",
  "enforcement",
  "engagement",
  "enrollment",
  "entertainment",
  "entity",
  "environment",
  "equality",
  "equation",
  "equipment",
  "equity",
  "ethnicity",
  "evidence",
  "excellence",
  "exhibition",
  "experience",
  "experiment",
  "expression",
  "facility",
  "federation",
  "fertility",
  "fiction",
  "finance",
  "fitness",
  "foundation",
  "fraction",
  "frequency",
  "friction",
  "function",
  "generation",
  "government",
  "governance",
  "gravity",
  "guidance",
  "happiness",
  "humanity",
  "identity",
  "illness",
  "imagination",
  "immigration",
  "incidence",
  "independence",
  "inequality",
  "infection",
  "inflammation",
  "information",
  "infrastructure",
  "inheritance",
  "injection",
  "inspiration",
  "instance",
  "institution",
  "instruction",
  "instrument",
  "insurance",
  "integrity",
  "intelligence",
  "intensity",
  "interaction",
  "intersection",
  "intervention",
  "intuition",
  "investment",
  "jurisdiction",
  "legislation",
  "liability",
  "limitation",
  "literacy",
  "location",
  "majority",
  "medication",
  "migration",
  "minority",
  "mission",
  "mobility",
  "moment",
  "momentum",
  "morbidity",
  "mortality",
  "motivation",
  "movement",
  "mutation",
  "nationality",
  "neighborhood",
  "notation",
  "nutrition",
  "obligation",
  "occasion",
  "occupation",
  "operation",
  "opinion",
  "opportunity",
  "organization",
  "orientation",
  "parliament",
  "patience",
  "pension",
  "performance",
  "personality",
  "petition",
  "phenomenon",
  "pigment",
  "pollution",
  "population",
  "portion",
  "position",
  "precipitation",
  "prescription",
  "prevalence",
  "priority",
  "probability",
  "production",
  "productivity",
  "proportion",
  "proposition",
  "prosperity",
  "province",
  "proximity",
  "publication",
  "qualification",
  "quality",
  "quantity",
  "question",
  "quotation",
  "radiation",
  "reaction",
  "reality",
  "recession",
  "reference",
  "regression",
  "regulation",
  "relation",
  "relationship",
  "reliability",
  "religion",
  "residence",
  "resistance",
  "resonance",
  "respiration",
  "revolution",
  "satisfaction",
  "science",
  "section",
  "security",
  "segment",
  "sensation",
  "sensitivity",
  "sentence",
  "sentiment",
  "sequence",
  "session",
  "settlement",
  "sexuality",
  "silence",
  "simulation",
  "situation",
  "society",
  "solution",
  "specificity",
  "stability",
  "statement",
  "station",
  "substance",
  "supplement",
  "surveillance",
  "television",
  "temperament",
  "tension",
  "territory",
  "testament",
  "tolerance",
  "tournament",
  "tradition",
  "transition",
  "translation",
  "treatment",
  "tuition",
  "unemployment",
  "uniformity",
  "university",
  "utility",
  "vacation",
  "validity",
  "variability",
  "variance",
  "variation",
  "vegetation",
  "velocity",
  "version",
  "violence",
  "visibility",
  "vision",
  "weakness",
  "wellness",
  "wilderness",
  "witness",
]);

/** The light verbs that carry a nominalization, by lemma: "make a decision", "conduct a review". */
const LIGHT_VERB_FORMS: Readonly<Record<string, readonly string[]>> = {
  achieve: ["achieve", "achieved", "achieves", "achieving"],
  cause: ["cause", "caused", "causes", "causing"],
  conduct: ["conduct", "conducted", "conducting", "conducts"],
  draw: ["draw", "drawing", "drawn", "draws", "drew"],
  exhibit: ["exhibit", "exhibited", "exhibiting", "exhibits"],
  express: ["express", "expressed", "expresses", "expressing"],
  gain: ["gain", "gained", "gaining", "gains"],
  give: ["gave", "give", "given", "gives", "giving"],
  have: ["had", "has", "have", "having"],
  hold: ["held", "hold", "holding", "holds"],
  lay: ["laid", "lay", "laying", "lays"],
  make: ["made", "make", "makes", "making"],
  offer: ["offer", "offered", "offering", "offers"],
  perform: ["perform", "performed", "performing", "performs"],
  place: ["place", "placed", "places", "placing"],
  provide: ["provide", "provided", "provides", "providing"],
  put: ["put", "puts", "putting"],
  raise: ["raise", "raised", "raises", "raising"],
  reach: ["reach", "reached", "reaches", "reaching"],
  show: ["show", "showed", "showing", "shown", "shows"],
  take: ["take", "taken", "takes", "taking", "took"],
  undertake: [
    "undertake",
    "undertaken",
    "undertakes",
    "undertaking",
    "undertook",
  ],
};

/** Each form of a light verb, mapped to its lemma. */
const LIGHT_VERBS: ReadonlyMap<string, string> = new Map(
  Object.entries(LIGHT_VERB_FORMS).flatMap(([lemma, forms]) =>
    forms.map((form) => [form, lemma] as const)
  )
);

interface VerbPair {
  lemma: string;
  forms: ReadonlySet<string>;
}

/** Two-word light verbs, keyed by their particle: "carried out", "came to", "led to". */
const LIGHT_VERB_PAIRS: Readonly<Record<string, readonly VerbPair[]>> = {
  about: [
    {
      lemma: "bring about",
      forms: new Set(["bring", "bringing", "brings", "brought"]),
    },
  ],
  at: [
    {
      lemma: "arrive at",
      forms: new Set(["arrive", "arrived", "arrives", "arriving"]),
    },
  ],
  in: [
    {
      lemma: "engage in",
      forms: new Set(["engage", "engaged", "engages", "engaging"]),
    },
    // "resulted in", but not "results in": "the results in Table 2" is a noun.
    { lemma: "result in", forms: new Set(["resulted", "resulting"]) },
  ],
  into: [
    {
      lemma: "enter into",
      forms: new Set(["enter", "entered", "entering", "enters"]),
    },
  ],
  out: [
    {
      lemma: "carry out",
      forms: new Set(["carried", "carries", "carry", "carrying"]),
    },
  ],
  to: [
    { lemma: "come to", forms: new Set(["came", "come", "comes", "coming"]) },
    { lemma: "lead to", forms: new Set(["lead", "leading", "leads", "led"]) },
  ],
};

const TAKE_FORMS = new Set(["take", "taken", "takes", "taking", "took"]);
const BE_FORMS = new Set([
  "am",
  "are",
  "be",
  "been",
  "being",
  "is",
  "was",
  "were",
]);
const MODALS = new Set([
  "can",
  "could",
  "may",
  "might",
  "must",
  "shall",
  "should",
  "will",
  "would",
]);
const HAVE_FORMS = new Set(["had", "has", "have"]);

/** Participles that, after a noun and a form of "be", make the passive light-verb phrase. */
const LIGHT_PARTICIPLES = new Set([
  "achieved",
  "conducted",
  "drawn",
  "gained",
  "given",
  "held",
  "made",
  "offered",
  "performed",
  "placed",
  "provided",
  "reached",
  "taken",
  "undertaken",
]);

const DETERMINERS = new Set([
  "a",
  "an",
  "any",
  "her",
  "his",
  "its",
  "more",
  "my",
  "no",
  "our",
  "some",
  "that",
  "the",
  "their",
  "these",
  "this",
  "those",
  "your",
]);

/** Words that cannot be the one modifier between a light verb and its noun. */
const NOT_A_MODIFIER = new Set([
  ...DETERMINERS,
  ...BE_FORMS,
  ...MODALS,
  "and",
  "as",
  "at",
  "but",
  "by",
  "for",
  "from",
  "he",
  "i",
  "in",
  "it",
  "not",
  "of",
  "on",
  "or",
  "she",
  "they",
  "to",
  "we",
  "with",
  "you",
]);

/** The preposition a light-verb phrase may end on: "conduct an investigation of". */
const TRAILING_PREPOSITIONS = new Set([
  "about",
  "at",
  "between",
  "for",
  "in",
  "into",
  "of",
  "on",
  "regarding",
  "to",
  "with",
]);

const POSSIBLE_SUFFIX = /(?:tion|sion|ment|ance|ence|ity|ness)$/;
const POSSIBLE_MIN_LENGTH = 8;
const DENSE_SENTENCE_MIN = 3;

/* ------------------------------------------------------------------ lookup */

interface NounEntry {
  /** The American singular, which keys the exemption lists: "characterization". */
  key: string;
  /** The singular as the text spells it: "characterisation". */
  noun: string;
  kind: "verb" | "adjective" | "phrase-only";
  /** "agree with", "be important", "refer to". */
  suggestion: string;
  /** The light verbs (by lemma) a phrase-only noun pairs with. Any, when absent. */
  verbs?: ReadonlySet<string>;
}

function pluralOf(noun: string): string {
  if (noun.endsWith("sis")) {
    return `${noun.slice(0, -3)}ses`;
  }
  if (noun.endsWith("y") && !"aeiou".includes(noun.at(-2) ?? "")) {
    return `${noun.slice(0, -1)}ies`;
  }
  if (noun.endsWith("ss") || noun.endsWith("ch") || noun.endsWith("sh")) {
    return `${noun}es`;
  }
  return `${noun}s`;
}

/** British spellings of the American keys: "-isation", "judgement", "fulfilment". */
function britishVariants(noun: string): string[] {
  const variants: string[] = [];
  if (noun.includes("ization")) {
    variants.push(noun.replace("ization", "isation"));
  }
  if (noun === "judgment" || noun === "acknowledgment") {
    variants.push(noun.replace("gment", "gement"));
  }
  if (noun === "fulfillment") {
    variants.push("fulfilment");
  }
  return variants;
}

function buildLookup(): Map<string, NounEntry> {
  const lookup = new Map<string, NounEntry>();
  const add = (
    key: string,
    entry: Pick<NounEntry, "kind" | "suggestion" | "verbs">
  ) => {
    for (const form of [key, ...britishVariants(key)]) {
      const value: NounEntry = { key, noun: form, ...entry };
      lookup.set(form, value);
      lookup.set(pluralOf(form), value);
    }
  };
  for (const [noun, [verb, lemmas]] of Object.entries(PHRASE_ONLY_NOUNS)) {
    add(noun, {
      kind: "phrase-only",
      suggestion: verb,
      verbs: new Set(lemmas),
    });
  }
  for (const [noun, adjective] of Object.entries(ADJECTIVE_NOUNS)) {
    add(noun, { kind: "adjective", suggestion: `be ${adjective}` });
  }
  for (const [noun, verb] of Object.entries(VERB_NOUNS)) {
    add(noun, { kind: "verb", suggestion: verb });
  }
  return lookup;
}

const LOOKUP = buildLookup();

/** "difficulties" → "difficulty", "assessments" → "assessment". Enough for suffix tests. */
function singular(word: string): string {
  if (word.endsWith("nesses")) {
    return word.slice(0, -2);
  }
  if (word.endsWith("ies")) {
    return `${word.slice(0, -3)}y`;
  }
  if (word.endsWith("s") && !word.endsWith("ss")) {
    return word.slice(0, -1);
  }
  return word;
}

/** The verb alone, without the preposition it takes: "agree with" → "agree". */
function bareVerb(suggestion: string): string {
  return suggestion.split(" ")[0] ?? suggestion;
}

/** "analyze" stays; "optimise" for "optimisation", so the advice matches the text. */
function localize(suggestion: string, form: string): string {
  if (form === "fulfilment") {
    return suggestion.replace("fulfill", "fulfil");
  }
  return form.includes("isation")
    ? suggestion.replace("ize", "ise")
    : suggestion;
}

/* ------------------------------------------------------------------ detection */

interface Context {
  text: string;
  tokens: Token[];
  sentences: Sentence[];
  /** Per sentence: unterminated, alone on its line and short — a heading. */
  headings: boolean[];
}

function lowerAt(ctx: Context, index: number): string {
  return ctx.tokens[index]?.lower ?? "";
}

function linked(ctx: Context, left: number, right: number): boolean {
  const a = ctx.tokens[left];
  const b = ctx.tokens[right];
  return Boolean(a && b) && adjacent(ctx.text, a as Token, b as Token);
}

function inHeading(ctx: Context, offset: number): boolean {
  const index = sentenceIndexAt(ctx.sentences, offset);
  return index >= 0 && ctx.headings[index] === true;
}

function isExempt(ctx: Context, index: number, key: string): boolean {
  const before = EXEMPT_AFTER[key];
  if (
    before &&
    linked(ctx, index - 1, index) &&
    before.includes(lowerAt(ctx, index - 1))
  ) {
    return true;
  }
  const after = EXEMPT_BEFORE[key];
  if (
    after &&
    linked(ctx, index, index + 1) &&
    after.includes(lowerAt(ctx, index + 1))
  ) {
    return true;
  }
  // "in the presence of" is a fixed phrase; "the presence of X" is a zombie.
  return (
    key === "presence" &&
    lowerAt(ctx, index - 1) === "the" &&
    lowerAt(ctx, index - 2) === "in"
  );
}

/** One optional modifier between determiner and noun: "make a final decision". */
function skipModifier(ctx: Context, cursor: number): number {
  const word = lowerAt(ctx, cursor);
  const isModifier =
    linked(ctx, cursor, cursor + 1) &&
    !NOT_A_MODIFIER.has(word) &&
    !LIGHT_VERBS.has(word) &&
    DETERMINERS.has(lowerAt(ctx, cursor - 1));
  return isModifier ? cursor - 1 : cursor;
}

interface LightVerb {
  /** Token index of the light verb, or of its first word. */
  first: number;
  lemma: string;
}

function pairLemma(word: string, previous: string): string | null {
  const pair = LIGHT_VERB_PAIRS[word]?.find((candidate) =>
    candidate.forms.has(previous)
  );
  if (pair) {
    return pair.lemma;
  }
  // "take into consideration" ("take into account" is not a nominalization)
  if (word === "into" && TAKE_FORMS.has(previous)) {
    return "take into";
  }
  // "there was an increase in", "there is a need for"
  return BE_FORMS.has(word) && previous === "there" ? "there be" : null;
}

/** The light verb before the noun at `index`, if there is one. */
function lightVerbBefore(ctx: Context, index: number): LightVerb | null {
  let cursor = skipModifier(ctx, index - 1);
  if (
    DETERMINERS.has(lowerAt(ctx, cursor)) &&
    linked(ctx, cursor, cursor + 1)
  ) {
    cursor -= 1;
  }
  if (!linked(ctx, cursor, cursor + 1)) {
    return null;
  }
  const word = lowerAt(ctx, cursor);
  const single = LIGHT_VERBS.get(word);
  if (single) {
    return { first: cursor, lemma: single };
  }
  if (!linked(ctx, cursor - 1, cursor)) {
    return null;
  }
  const lemma = pairLemma(word, lowerAt(ctx, cursor - 1));
  return lemma ? { first: cursor - 1, lemma } : null;
}

/** Start of "is in agreement with" / "is of importance" before the noun, or -1. */
function beFrameStart(ctx: Context, index: number, particle: string): number {
  let cursor = index - 1;
  // "of great importance", "in full agreement"
  if (lowerAt(ctx, cursor) !== particle && linked(ctx, cursor, cursor + 1)) {
    cursor -= 1;
  }
  if (lowerAt(ctx, cursor) !== particle || !linked(ctx, cursor, cursor + 1)) {
    return -1;
  }
  const verb = cursor - 1;
  return BE_FORMS.has(lowerAt(ctx, verb)) && linked(ctx, verb, cursor)
    ? verb
    : -1;
}

/** End index (inclusive) of "was made", "has been conducted", "will be given" after the noun. */
function passiveEnd(ctx: Context, index: number): number {
  let cursor = index + 1;
  const helper = lowerAt(ctx, cursor);
  if (HAVE_FORMS.has(helper) || MODALS.has(helper)) {
    if (!linked(ctx, cursor - 1, cursor)) {
      return -1;
    }
    cursor += 1;
  }
  if (
    !(BE_FORMS.has(lowerAt(ctx, cursor)) && linked(ctx, cursor - 1, cursor))
  ) {
    return -1;
  }
  const participle = cursor + 1;
  if (!linked(ctx, cursor, participle)) {
    return -1;
  }
  const word = lowerAt(ctx, participle);
  if (LIGHT_PARTICIPLES.has(word)) {
    return participle;
  }
  const carriedOut =
    word === "carried" &&
    lowerAt(ctx, participle + 1) === "out" &&
    linked(ctx, participle, participle + 1);
  return carriedOut ? participle + 1 : -1;
}

function makeMatch(
  ctx: Context,
  span: { first: number; last: number },
  kind: NominalizationKind,
  noun: string,
  suggestion: string
): NominalizationMatch {
  const start = ctx.tokens[span.first].start;
  const end = ctx.tokens[span.last].end;
  return {
    start,
    end,
    text: ctx.text.slice(start, end),
    kind,
    noun,
    suggestion,
  };
}

/** A verb + preposition suggestion fits only when the phrase ends on that preposition. */
function phraseSuggestion(entry: NounEntry, trailing: string): string {
  if (entry.kind === "adjective") {
    return entry.suggestion;
  }
  const [verb, preposition] = entry.suggestion.split(" ");
  const chosen =
    preposition && trailing === preposition ? entry.suggestion : verb;
  return localize(chosen ?? entry.suggestion, entry.noun);
}

function trailingPreposition(ctx: Context, index: number): number {
  const next = index + 1;
  return TRAILING_PREPOSITIONS.has(lowerAt(ctx, next)) &&
    linked(ctx, index, next)
    ? next
    : -1;
}

function lightVerbPhrase(
  ctx: Context,
  index: number,
  entry: NounEntry
): NominalizationMatch | null {
  const verb = lightVerbBefore(ctx, index);
  if (!verb) {
    return null;
  }
  // "take into consideration" is the only "take into" phrase here.
  if (verb.lemma === "take into" && entry.key !== "consideration") {
    return null;
  }
  if (entry.verbs && !entry.verbs.has(verb.lemma)) {
    return null;
  }
  const prep = trailingPreposition(ctx, index);
  const last = prep >= 0 ? prep : index;
  return makeMatch(
    ctx,
    { first: verb.first, last },
    "phrase",
    entry.noun,
    phraseSuggestion(entry, lowerAt(ctx, prep))
  );
}

function beFramePhrase(
  ctx: Context,
  index: number,
  lower: string
): NominalizationMatch | null {
  const noun = singular(lower);
  const adjective = BE_OF_NOUNS[noun];
  if (adjective) {
    const first = beFrameStart(ctx, index, "of");
    if (first >= 0) {
      return makeMatch(
        ctx,
        { first, last: index },
        "phrase",
        noun,
        `be ${adjective}`
      );
    }
  }
  const verb = BE_IN_NOUNS[noun];
  if (!verb) {
    return null;
  }
  const first = beFrameStart(ctx, index, "in");
  if (first < 0) {
    return null;
  }
  const prep = trailingPreposition(ctx, index);
  return makeMatch(
    ctx,
    { first, last: prep >= 0 ? prep : index },
    "phrase",
    noun,
    verb
  );
}

function passivePhrase(
  ctx: Context,
  index: number,
  entry: NounEntry
): NominalizationMatch | null {
  const last = passiveEnd(ctx, index);
  if (last < 0) {
    return null;
  }
  const suggestion =
    entry.kind === "adjective" ? entry.suggestion : bareVerb(entry.suggestion);
  return makeMatch(
    ctx,
    { first: index, last },
    "phrase",
    entry.noun,
    localize(suggestion, entry.noun)
  );
}

function possibleMatch(
  ctx: Context,
  index: number,
  lower: string
): NominalizationMatch | null {
  const noun = singular(lower);
  const framed =
    lowerAt(ctx, index + 1) === "of" && linked(ctx, index, index + 1);
  if (
    !framed ||
    noun.length < POSSIBLE_MIN_LENGTH ||
    !POSSIBLE_SUFFIX.test(noun) ||
    EVERYDAY_NOUNS.has(noun)
  ) {
    return null;
  }
  return makeMatch(ctx, { first: index, last: index }, "possible", noun, "");
}

function knownNounMatch(
  ctx: Context,
  index: number,
  entry: NounEntry
): NominalizationMatch | null {
  const phrase =
    lightVerbPhrase(ctx, index, entry) ?? passivePhrase(ctx, index, entry);
  if (phrase) {
    return phrase;
  }
  if (entry.kind === "phrase-only") {
    return null;
  }
  const suggestion =
    entry.kind === "verb" ? bareVerb(entry.suggestion) : entry.suggestion;
  return makeMatch(
    ctx,
    { first: index, last: index },
    "noun",
    entry.noun,
    localize(suggestion, entry.noun)
  );
}

function matchAt(ctx: Context, index: number): NominalizationMatch | null {
  const token = ctx.tokens[index];
  if (
    isHyphenated(token.text) ||
    isCapitalizedMidSentence(ctx.text, token.start) ||
    inHeading(ctx, token.start)
  ) {
    return null;
  }
  const lower = stripPossessive(token.lower);
  const framed = beFramePhrase(ctx, index, lower);
  if (framed) {
    return framed;
  }
  const entry = LOOKUP.get(lower);
  if (entry) {
    return isExempt(ctx, index, entry.key)
      ? null
      : knownNounMatch(ctx, index, entry);
  }
  return possibleMatch(ctx, index, lower);
}

/** A phrase reaches back over words already scanned and replaces what it covers. */
function collectMatches(ctx: Context): NominalizationMatch[] {
  const matches: NominalizationMatch[] = [];
  for (let index = 0; index < ctx.tokens.length; index += 1) {
    const match = matchAt(ctx, index);
    if (!match) {
      continue;
    }
    while ((matches.at(-1)?.end ?? -1) > match.start) {
      matches.pop();
    }
    matches.push(match);
  }
  return matches;
}

function denseSentences(
  sentences: Sentence[],
  matches: NominalizationMatch[]
): DenseSentence[] {
  const counts = new Map<number, number>();
  for (const match of matches) {
    const index = sentenceIndexAt(sentences, match.start);
    if (index >= 0) {
      counts.set(index, (counts.get(index) ?? 0) + 1);
    }
  }
  return [...counts.entries()]
    .filter(([, count]) => count >= DENSE_SENTENCE_MIN)
    .sort((a, b) => b[1] - a[1] || a[0] - b[0])
    .map(([index, count]) => {
      const sentence = sentences[index];
      return {
        start: sentence.start,
        end: sentence.end,
        text: sentence.text,
        count,
      };
    });
}

/** A table row names the noun, so it shows the verb or adjective without a preposition. */
function rowSuggestion(match: NominalizationMatch): string {
  return match.suggestion.startsWith("be ")
    ? match.suggestion
    : bareVerb(match.suggestion);
}

function byNoun(matches: NominalizationMatch[]): NounTally[] {
  const rows = new Map<string, NounTally>();
  for (const match of matches) {
    const row = rows.get(match.noun);
    if (row) {
      row.count += 1;
      continue;
    }
    rows.set(match.noun, {
      noun: match.noun,
      count: 1,
      suggestion: rowSuggestion(match),
      kind: match.kind,
    });
  }
  return [...rows.values()].sort(
    (a, b) => b.count - a.count || a.noun.localeCompare(b.noun)
  );
}

export function findNominalizations(text: string): NominalizationReport {
  const sentences = splitSentences(text);
  const ctx: Context = {
    text,
    tokens: wordTokens(text),
    sentences,
    headings: headingFlags(sentences),
  };
  const matches = collectMatches(ctx);
  const words = countWords(text);
  return {
    words,
    matches,
    phrases: matches.filter((m) => m.kind === "phrase").length,
    nouns: matches.filter((m) => m.kind === "noun").length,
    possible: matches.filter((m) => m.kind === "possible").length,
    density: ratePer(matches.length, words, 100),
    denseSentences: denseSentences(sentences, matches),
    byNoun: byNoun(matches),
  };
}
